import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { createStructuredSelector } from 'reselect';
import { SCButton, SCLoader, SCPagination } from 'rollup-allspark';
import NotificationImg from '../../../assets/images/Notification.png';
import Drawer from '../../../components/Drawer';
import HelpPopover from '../../../components/HelpPopover';
import {
  EXPORT_TYPE,
  ORDER_BREAKDOWN_TYPE,
} from '../../../config/constants/analytics';
import { DEFAULT_PAGE_SIZES } from '../../../config/constants/pagination';
import { ORDER_BREAKDOWN_HELP } from '../../../config/HelpPopoverTexts/nativeAnalyticsPopoverTexts';
import useAnalytics from '../../../hooks/useAnalytics';
import { cn, isEmpty, printf } from '../../../utils';
import {
  getXLSX,
  loadAnalyticsCsv,
  loadList,
  loadOrdersData,
  updateEntity,
  updatePagination,
} from '../actions';
import {
  EXPORT_ORDER_BREAKDOWN_ITEM,
  EXPORT_ORDER_BREAKDOWN_ORDER,
  PREV_REPORT_LIST,
} from '../sagaMap';
import * as selectors from '../selectors';
import OrderTableHeader from './OrderTableHeader';
import OrderTableRow from './OrderTableRow';
import PreviousReports from './PreviousReports';

const OrderTable = ({
  ordersData,
  ordersDataLoading,
  paginationObj,
  handleUpdatePagination,
  getOrdersData,
  handleAnalyticsCsvDownload,
  isMoreThan30Days,
  downloadXLSX,
  selectedBusiness,
  getPrevReports,
  updateField,
  isAnalyticsCsvDownloading,
  isXlsxDownloading,
}) => {
  const { getAnalyticsParams } = useAnalytics();

  const { orders, pagination } = ordersData || {};
  let renderOrdersDataRow = null;
  const { pageNo, pageSize } = paginationObj || {};
  const [isOrderBreakdownPopoverOpen, setIsOrderBreakdownPopoverOpen] =
    useState(false);
  const [showMoreThan30DaysPlaceholder, setShowMoreThan30DaysPlaceholder] =
    useState(false);
  const [isReportDrawerOpen, setIsReportDrawerOpen] = useState(false);
  const [reportBreakdownType, setReportBreakdownType] = useState(
    ORDER_BREAKDOWN_TYPE.ORDER,
  );
  const [activeDownloadKey, setActiveDownloadKey] = useState(null);

  const getDownloadKey = (breakdownType, exportType) =>
    `${breakdownType}-${exportType}`;

  useEffect(() => {
    if (!isAnalyticsCsvDownloading && !isXlsxDownloading) {
      setActiveDownloadKey(null);
    }
  }, [isAnalyticsCsvDownloading, isXlsxDownloading]);

  const getFilteredOrderData = () => {
    const params = { ...getAnalyticsParams() };

    params.page_no = pageNo;
    params.page_size = pageSize;

    if (params.brand_id || params.branch_id) {
      getOrdersData(params);
    }
  };

  useEffect(getFilteredOrderData, [paginationObj]);

  useEffect(() => {
    setShowMoreThan30DaysPlaceholder(isMoreThan30Days);
  }, [isMoreThan30Days]);

  const onAnalyticsDownloadButtonClick = (
    breakdownType = ORDER_BREAKDOWN_TYPE.ORDER,
  ) => {
    setActiveDownloadKey(getDownloadKey(breakdownType, EXPORT_TYPE.CSV));
    const params = {
      ...getAnalyticsParams(),
      export_option: EXPORT_TYPE.CSV,
      breakdownType,
    };
    handleAnalyticsCsvDownload(params);
  };

  const onXlsxDownloadButtonClick = (
    breakdownType = ORDER_BREAKDOWN_TYPE.ORDER,
  ) => {
    setActiveDownloadKey(getDownloadKey(breakdownType, EXPORT_TYPE.XLSX));
    const params = {
      ...getAnalyticsParams(),
      export_option: EXPORT_TYPE.XLSX,
      breakdownType,
    };

    const map =
      breakdownType === ORDER_BREAKDOWN_TYPE.ORDER
        ? EXPORT_ORDER_BREAKDOWN_ORDER
        : EXPORT_ORDER_BREAKDOWN_ITEM;

    downloadXLSX({
      ...map,
      params,
      fileName: `Analytics-${breakdownType}`,
    });
  };

  const isAnyExportInProgress =
    (isAnalyticsCsvDownloading || isXlsxDownloading) && activeDownloadKey;

  const isDownloadButtonLoading = (breakdownType, exportType) => {
    const downloadKey = getDownloadKey(breakdownType, exportType);
    const isExportDownloading =
      exportType === EXPORT_TYPE.CSV
        ? isAnalyticsCsvDownloading
        : isXlsxDownloading;

    return activeDownloadKey === downloadKey && isExportDownloading;
  };

  const renderExportButton = (breakdownType, exportType, onClick) => {
    const isLoading = isDownloadButtonLoading(breakdownType, exportType);
    const isDisabled = isAnyExportInProgress;
    const label = exportType.toUpperCase();

    return (
      <SCButton
        variant="secondary"
        size="sm"
        className="h-6 w-auto px-4 text-xs bg-grey-light hover:bg-grey text-black rounded-lg"
        action={onClick}
        disabled={isDisabled}
      >
        <div className="flex items-center gap-x-1 font-medium min-w-[42px] justify-center">
          {isLoading ? (
            <SCLoader lineWidth={2} diameter={16} />
          ) : (
            <>
              <i className="kt-download" />
              <p>{label}</p>
            </>
          )}
        </div>
      </SCButton>
    );
  };

  const onDrawerClose = () => {
    setIsReportDrawerOpen(false);
    updateField({
      previousOrderReports: [],
    });
  };

  const onReportBreakdownTypeChange = rType => {
    updateField({
      previousOrderReports: [],
    });
    setReportBreakdownType(rType);
    onPrevReportBtnClicked(rType);
  };

  const onPrevReportBtnClicked = (rType = ORDER_BREAKDOWN_TYPE.ORDER) => {
    setReportBreakdownType(rType);
    setIsReportDrawerOpen(true);
    getPrevReports({
      ...PREV_REPORT_LIST,
      url: printf(
        PREV_REPORT_LIST.url,
        selectedBusiness?.id,
        rType.toLowerCase(),
      ),
    });
  };

  if (ordersDataLoading) {
    renderOrdersDataRow = (
      <tr>
        <td
          rowSpan="10"
          colSpan="16"
          className="p-5"
          style={{ height: '380px' }}
        >
          <SCLoader lineWidth={5} diameter={50} />
        </td>
      </tr>
    );
  } else {
    renderOrdersDataRow =
      orders && orders.length ? (
        orders.map(order => <OrderTableRow key={order.id} orderData={order} />)
      ) : (
        <tr className="text-center">
          <td colSpan="14" className="p-5 text-sm text-grey-dark">
            No data found
          </td>
        </tr>
      );
  }

  const getCsvInEmailEl = (
    <tr className="text-center">
      <td colSpan="16" className="p-5 text-2xl text-grey-darker h-20">
        <div className="flex justify-center items-center flex-col w-full gap-y-1">
          <img
            src={NotificationImg}
            alt="Notification"
            width={150}
            height={150}
          />
          <div className="text-xl text-black">Date range Exceeds 30 Days</div>
          <p className="text-grey-darker text-sm">
            Let us send the data in your mail.
          </p>
          <div className="flex gap-x-4 divide-x divide-grey mt-8">
            <div className="flex flex-col gap-y-4">
              <div className="text-sm text-grey-darker">Order Breakdown</div>
              <SCButton
                variant="secondary"
                className="rounded-lg bg-grey-light text-black hover:bg-grey gap-x-1 w-auto flex items-center px-4 text-sm"
                action={() =>
                  onAnalyticsDownloadButtonClick(ORDER_BREAKDOWN_TYPE.ORDER)
                }
              >
                <i className="flex items-center sc-mail" />
                Get the CSV in Email
              </SCButton>
              <SCButton
                variant="secondary"
                className="rounded-lg bg-grey-light text-black hover:bg-grey gap-x-1 w-auto flex items-center px-4 text-sm"
                action={() =>
                  onXlsxDownloadButtonClick(ORDER_BREAKDOWN_TYPE.ORDER)
                }
              >
                <i className="flex items-center sc-mail" />
                Get the XLSX in Email
              </SCButton>
            </div>
            <div className="flex flex-col gap-y-4 pl-4">
              <div className="text-sm text-grey-darker">Item Breakdown</div>
              <SCButton
                variant="secondary"
                className="rounded-lg bg-grey-light text-black hover:bg-grey gap-x-1 w-auto flex items-center px-4 text-sm"
                action={() =>
                  onAnalyticsDownloadButtonClick(ORDER_BREAKDOWN_TYPE.ITEM)
                }
              >
                <i className="flex items-center sc-mail" />
                Get the CSV in Email
              </SCButton>
              <SCButton
                variant="secondary"
                className="rounded-lg bg-grey-light text-black hover:bg-grey gap-x-1 w-auto flex items-center px-4 text-sm"
                action={() =>
                  onXlsxDownloadButtonClick(ORDER_BREAKDOWN_TYPE.ITEM)
                }
              >
                <i className="flex items-center sc-mail" />
                Get the XLSX in Email
              </SCButton>
            </div>
          </div>
        </div>
      </td>
    </tr>
  );

  const renderPagination =
    !ordersDataLoading && orders && orders.length && !isMoreThan30Days ? (
      <SCPagination
        total={pagination && pagination.total ? pagination.total : 0}
        page={pageNo}
        size={pageSize}
        sizes={DEFAULT_PAGE_SIZES}
        onPageChange={handleUpdatePagination}
      />
    ) : null;

  const renderDownloadButton =
    orders && orders.length ? (
      <div className="flex gap-x-4 divide-x divide-grey">
        <div className="flex flex-col gap-y-1">
          <div className="text-xs">Order Breakdown</div>
          <div className="flex gap-x-2">
            {renderExportButton(
              ORDER_BREAKDOWN_TYPE.ORDER,
              EXPORT_TYPE.CSV,
              () => onAnalyticsDownloadButtonClick(),
            )}
            {renderExportButton(
              ORDER_BREAKDOWN_TYPE.ORDER,
              EXPORT_TYPE.XLSX,
              () => onXlsxDownloadButtonClick(),
            )}
          </div>
        </div>
        <div className="flex flex-col gap-y-1 pl-4">
          <div className="text-xs">Item Breakdown</div>
          <div className="flex gap-x-2">
            {renderExportButton(
              ORDER_BREAKDOWN_TYPE.ITEM,
              EXPORT_TYPE.CSV,
              () => onAnalyticsDownloadButtonClick(ORDER_BREAKDOWN_TYPE.ITEM),
            )}
            {renderExportButton(
              ORDER_BREAKDOWN_TYPE.ITEM,
              EXPORT_TYPE.XLSX,
              () => onXlsxDownloadButtonClick(ORDER_BREAKDOWN_TYPE.ITEM),
            )}
          </div>
        </div>
      </div>
    ) : null;

  return (
    <div
      className="bg-white w-full rounded-lg border border-grey"
      style={{ boxShadow: 'var(--sc-shadow-card)' }}
    >
      <div className="w-full flex justify-between items-center p-4">
        <div className="text-base font-semibold flex items-center gap-x-2">
          Order Breakdown
          <HelpPopover
            isPopoverOpen={isOrderBreakdownPopoverOpen}
            position="right"
            isHtmlElement
            content={ORDER_BREAKDOWN_HELP}
            setIsPopoverOpen={setIsOrderBreakdownPopoverOpen}
            iconClassNames="sc-information cursor-pointer flex items-center"
          />
        </div>
        <div className="flex items-end gap-x-4 divide-x-2 divide-grey">
          {renderDownloadButton}
          <div className="pl-4">
            <SCButton
              variant="secondary"
              className="bg-grey-light text-black hover:bg-grey rounded-lg w-auto px-4 font-medium"
              action={() => onPrevReportBtnClicked()}
              disabled={isEmpty(selectedBusiness)}
            >
              Previous Reports
            </SCButton>
          </div>
        </div>
      </div>
      <div className="overflow-x-auto mb-4">
        <table className="table-auto w-full border-y border-grey">
          <thead>
            <OrderTableHeader />
          </thead>
          <tbody>
            {showMoreThan30DaysPlaceholder
              ? getCsvInEmailEl
              : renderOrdersDataRow}
          </tbody>
        </table>
      </div>
      <div className="w-full flex justify-center p-4">{renderPagination}</div>

      <Drawer
        anchor="right"
        className="h-full w-2/5"
        isOpen={isReportDrawerOpen}
        onClose={onDrawerClose}
      >
        <div className="border-b h-16 flex items-center justify-between px-4 text-base gap-x-4">
          <div className="flex items-center gap-x-4 font-medium">
            <i
              className="kt-x text-xl cursor-pointer"
              role="presentation"
              onClick={onDrawerClose}
            />
            Previous Reports
          </div>
          <div className="flex gap-x-2 text-xs bg-grey-light rounded-lg p-1">
            <div
              className={cn({
                'px-4 py-2 rounded-lg cursor-pointer': true,
                'rounded-lg bg-white':
                  reportBreakdownType === ORDER_BREAKDOWN_TYPE.ORDER,
              })}
              role="presentation"
              onClick={() =>
                onReportBreakdownTypeChange(ORDER_BREAKDOWN_TYPE.ORDER)
              }
            >
              Order Breakdown
            </div>
            <div
              className={cn({
                'px-4 py-2 rounded-lg cursor-pointer': true,
                'rounded-lg bg-white':
                  reportBreakdownType === ORDER_BREAKDOWN_TYPE.ITEM,
              })}
              role="presentation"
              onClick={() =>
                onReportBreakdownTypeChange(ORDER_BREAKDOWN_TYPE.ITEM)
              }
            >
              Item Breakdown
            </div>
          </div>
        </div>
        <PreviousReports />
      </Drawer>
    </div>
  );
};

OrderTable.propTypes = {
  ordersData: PropTypes.object,
  ordersDataLoading: PropTypes.bool,
  paginationObj: PropTypes.object,
  handleUpdatePagination: PropTypes.func,
  getOrdersData: PropTypes.func,
  handleAnalyticsCsvDownload: PropTypes.func,
  isMoreThan30Days: PropTypes.bool,
  downloadXLSX: PropTypes.func,
  getPrevReports: PropTypes.func,
  selectedBusiness: PropTypes.object,
  updateField: PropTypes.func,
  isAnalyticsCsvDownloading: PropTypes.bool,
  isXlsxDownloading: PropTypes.bool,
};

const mapStateToProps = createStructuredSelector({
  ordersData: selectors.makeSelectOrdersData(),
  ordersDataLoading: selectors.makeSelectOrdersDataLoading(),
  paginationObj: selectors.makeSelectPaginationObj(),
  analyticsStartDate: selectors.makeSelectAnalyticsStartDate(),
  analyticsEndDate: selectors.makeSelectAnalyticsEndDate(),
  isMoreThan30Days: selectors.makeSelectIsMoreThan30Days(),
  selectedBusiness: selectors.makeSelectProperty('selectedBusiness'),
  isAnalyticsCsvDownloading: selectors.makeSelectAnalyticsCsvDownloading(),
  isXlsxDownloading: selectors.makeSelectXlsxDownloading(),
});

const mapDispatchToProps = dispatch => ({
  handleUpdatePagination: (page, size) =>
    dispatch(updatePagination(page, size)),
  getOrdersData: params => dispatch(loadOrdersData(params)),
  handleAnalyticsCsvDownload: params => dispatch(loadAnalyticsCsv(params)),
  downloadXLSX: params => dispatch(getXLSX(params)),
  getPrevReports: params => dispatch(loadList(params)),
  updateField: params => dispatch(updateEntity(params)),
});

const withConnect = connect(mapStateToProps, mapDispatchToProps);

export default compose(withConnect)(OrderTable);
